import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { Codebook, CodebookDetail, CodebookService, NamingLevel } from '../services/codebook.service';

@Component({
  selector: 'app-codebooks',
  templateUrl: './codebooks.component.html',
  styleUrls: ['./codebooks.component.css'],
  standalone: false,
})
export class CodebooksComponent implements OnInit, AfterViewInit {
  codebooks: MatTableDataSource<Codebook> = new MatTableDataSource();
  isLoading = false;
  displayedColumns = ['name', 'revision', 'description', 'namingLevels', 'lastUpdatedAt', 'actions'];

  @ViewChild(MatSort) sort: MatSort;

  constructor(private codebookService: CodebookService, public dialog: MatDialog, public snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.fetchCodebooks();
  }

  ngAfterViewInit(): void {
    this.codebooks.sort = this.sort;
  }

  fetchCodebooks(): void {
    this.isLoading = true;
    this.codebookService.getCodebooks().subscribe(
      (data) => {
        for (const item of data) {
          if (typeof item.codebook === 'string') {
            try {
              item.codebook = JSON.parse(item.codebook as any);
            } catch (e) {
              console.error('Failed to parse codebook string:', e);
            }
          }
        }
        this.codebooks.data = data;
        this.isLoading = false;
      },
      (err: HttpErrorResponse) => {
        this.snackBar.open(
          `Error fetching codebooks: ${err.status} ${err.statusText} - ${JSON.stringify(err.error)}`,
          null,
          { duration: 10000 }
        );
        console.error(err);
      }
    );
  }

  openCreateDialog(): void {
    const ref = this.dialog.open(CodebookEditDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { codebook: null },
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved) this.fetchCodebooks();
    });
  }

  openEditDialog(codebook: Codebook): void {
    const ref = this.dialog.open(CodebookEditDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { codebook },
    });
    ref.afterClosed().subscribe((saved) => {
      if (saved) this.fetchCodebooks();
    });
  }

  deleteCodebook(codebook: Codebook): void {
    const ref = this.dialog.open(ConfirmDialogComponent);
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.codebookService.deleteCodebook(codebook.id).subscribe(
          () => {
            this.snackBar.open('Codebook deleted', null, { duration: 4000 });
            this.fetchCodebooks();
          },
          (err: HttpErrorResponse) => {
            this.snackBar.open(`Error deleting codebook: ${JSON.stringify(err.error)}`, null, { duration: 10000 });
            console.error(err);
          }
        );
      }
    });
  }

  formatDate(dateString: string): string {
    return dateString ? new Date(dateString).toISOString().split('T')[0] : '';
  }

  getNamingLevelsSummary(codebook: Codebook): string {
    const levels = codebook.codebook?.namingLevels;
    if (!levels || levels.length === 0) return 'None';
    return levels.map((l) => l.name).join(', ');
  }
}

@Component({
  selector: 'app-codebook-edit-dialog',
  templateUrl: './codebook-edit-dialog.component.html',
  styleUrls: ['./codebook-edit-dialog.component.css'],
  standalone: false,
})
export class CodebookEditDialogComponent {
  detail: CodebookDetail;
  isEdit: boolean;
  editMode: 'form' | 'json' = 'form';
  jsonContent: string = '';
  jsonError: string = '';

  private static readonly Template: CodebookDetail = {
    name: '',
    revision: 1,
    description: '',
    namingLevels: [
      {
        levelNumber: 0,
        name: '',
        description: '',
        canBeOmitted: false,
        validationRule: { validationType: 'Regex', regex: '', allowedValues: [], description: '' },
      },
    ],
  };

  constructor(
    public dialogRef: MatDialogRef<CodebookEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { codebook: Codebook | null },
    private codebookService: CodebookService,
    public snackBar: MatSnackBar
  ) {
    this.isEdit = !!data.codebook;
    if (data.codebook?.codebook) {
      let src = data.codebook.codebook;
      if (typeof src === 'string') {
        src = JSON.parse(src as any);
      }
      this.detail = JSON.parse(JSON.stringify(src));
    } else if (data.codebook) {
      const cb = data.codebook;
      this.detail = {
        name: cb.name || '',
        revision: (cb as any).revision || 1,
        description: (cb as any).description || '',
        namingLevels: (cb as any).namingLevels || [],
      };
    } else {
      this.detail = JSON.parse(JSON.stringify(CodebookEditDialogComponent.Template));
    }
    this.ensureValidationRules();
    this.onLevelNumberChange();
  }

  switchMode(mode: 'form' | 'json'): void {
    if (mode === this.editMode) return;

    if (mode === 'json') {
      // Form → JSON: serialize current detail
      this.jsonContent = JSON.stringify(this.detail, null, 2);
      this.jsonError = '';
    } else {
      // JSON → Form: parse JSON back into detail
      try {
        this.detail = JSON.parse(this.jsonContent);
        this.ensureValidationRules();
        this.onLevelNumberChange();
        this.jsonError = '';
      } catch (e) {
        this.jsonError = `Cannot switch: invalid JSON — ${e}`;
        return;
      }
    }
    this.editMode = mode;
  }

  private static readonly VALIDATION_TYPE_MAP: Record<string, string> = {
    '0': 'Regex',
    '1': 'Regex',
    '2': 'AllowedValues',
    none: 'Regex',
    regex: 'Regex',
    allowedvalues: 'AllowedValues',
    allowedValues: 'AllowedValues',
    None: 'Regex',
    Regex: 'Regex',
    AllowedValues: 'AllowedValues',
  };

  private ensureValidationRules(): void {
    if (!this.detail.namingLevels) {
      this.detail.namingLevels = [];
    }
    for (const level of this.detail.namingLevels) {
      if (!level.validationRule) {
        level.validationRule = { validationType: 'Regex', regex: '', allowedValues: [], description: '' };
      }
      // Normalize validationType from numeric/variant casing to expected string
      const raw = String(level.validationRule.validationType);
      level.validationRule.validationType = CodebookEditDialogComponent.VALIDATION_TYPE_MAP[raw] || raw;
      if (!level.validationRule.allowedValues) {
        level.validationRule.allowedValues = [];
      }
    }
  }

  addLevel(): void {
    this.detail.namingLevels.push({
      levelNumber: this.detail.namingLevels.length,
      name: '',
      description: '',
      canBeOmitted: false,
      validationRule: { validationType: 'Regex', regex: '', allowedValues: [], description: '' },
    });
  }

  removeLevel(index: number): void {
    this.detail.namingLevels.splice(index, 1);
    this.reassignLevelNumbers();
  }

  onLevelNumberChange(): void {
    this.detail.namingLevels.sort((a, b) => a.levelNumber - b.levelNumber);
    this.reassignLevelNumbers();
  }

  private reassignLevelNumbers(): void {
    this.detail.namingLevels.forEach((level, i) => (level.levelNumber = i));
  }

  addAllowedValue(level: NamingLevel): void {
    level.validationRule.allowedValues.push({ value: '', description: '' });
  }

  removeAllowedValue(level: NamingLevel, index: number): void {
    level.validationRule.allowedValues.splice(index, 1);
  }

  onSaveClick(): void {
    let detail: CodebookDetail;

    if (this.editMode === 'json') {
      this.jsonError = '';
      try {
        detail = JSON.parse(this.jsonContent);
      } catch (e) {
        this.jsonError = `Invalid JSON: ${e}`;
        return;
      }
    } else {
      detail = this.detail;
    }

    const payload: CodebookDetail = {
      name: detail.name,
      revision: detail.revision,
      description: detail.description,
      namingLevels: detail.namingLevels,
    };

    const request$ = this.isEdit
      ? this.codebookService.updateCodebook(this.data.codebook.id, payload)
      : this.codebookService.createCodebook(payload);

    request$.subscribe(
      () => {
        this.snackBar.open(`Codebook ${this.isEdit ? 'updated' : 'created'} successfully`, null, { duration: 4000 });
        this.dialogRef.close(true);
      },
      (err: HttpErrorResponse) => {
        this.snackBar.open(`Error: ${JSON.stringify(err.error)}`, null, { duration: 10000 });
        console.error(err);
      }
    );
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
